import { useEffect, useState, useCallback } from "react";
import { Loader2, CreditCard, XCircle, AlertTriangle, RotateCcw, Wrench, TrendingUp, FlaskConical, Clock, Users, CheckCircle2, Ban, Save } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { StatCard, StatusBadge } from "@/components/admin/_shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TRIAL_PRESETS = [
  { label: "3 days (production)", seconds: 259200 },
  { label: "10 minutes", seconds: 600 },
  { label: "5 minutes", seconds: 300 },
  { label: "1 minute", seconds: 60 },
];

export default function AdminBilling() {
  const [d, setD] = useState(null);
  const [report, setReport] = useState(null);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      adminApi.get("/admin/billing"), adminApi.get("/admin/billing-report"), adminApi.get("/admin/billing-settings"),
    ]).then(([a, b, c]) => { setD(a.data); setReport(b.data); setSettings(c.data); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async (patch) => {
    setSaving(true);
    try { const r = await adminApi.put("/admin/billing-settings", patch); setSettings(r.data); toast.success("Billing settings updated"); }
    catch { toast.error("Could not update settings"); } finally { setSaving(false); }
  };

  if (!d || !report || !settings) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const c = report.counts;

  return (
    <div data-testid="admin-billing">
      <h1 className="font-heading text-2xl font-bold tracking-tight">Billing &amp; Subscriptions</h1>

      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="billing-mrr" label="Monthly Revenue" value={`£${d.mrr.toLocaleString()}`} icon={TrendingUp} accent="violet" />
        <StatCard testid="billing-trialing" label="Trial Users" value={c.trialing || 0} icon={Clock} accent="blue" />
        <StatCard testid="billing-active" label="Active Subscriptions" value={c.active || 0} icon={CheckCircle2} accent="emerald" />
        <StatCard testid="billing-pastdue" label="Payment Retrying" value={c.past_due || 0} icon={RotateCcw} accent="amber" />
        <StatCard testid="billing-suspended" label="Suspended (Unpaid)" value={c.unpaid || 0} icon={Ban} accent="rose" />
        <StatCard testid="billing-cancelled" label="Cancelled" value={c.canceled || 0} icon={XCircle} accent="slate" />
        <StatCard testid="billing-awaiting" label="Awaiting Activation" value={c.payment_verified || 0} icon={Users} accent="slate" />
        <StatCard testid="billing-failed" label="Failed Payments" value={(report.failed_payments || []).length} icon={AlertTriangle} accent="amber" />
      </div>

      {/* Hidden testing controls */}
      <div className="mt-6 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/50 p-5" data-testid="billing-test-controls">
        <div className="flex items-center gap-2"><FlaskConical className="h-5 w-5 text-amber-600" /><h2 className="font-heading font-bold tracking-tight text-amber-900">Billing test controls (admin only)</h2></div>
        <p className="text-sm text-amber-800/80 mt-1">Override the trial duration and simulate charges to test automatic billing, retries and failed-payment recovery without waiting 3 days. Never visible to customers. Production default is 3 days.</p>
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Trial duration (current: {settings.trial_seconds >= 86400 ? `${Math.round(settings.trial_seconds / 86400)} day(s)` : `${Math.round(settings.trial_seconds / 60)} min`})</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {TRIAL_PRESETS.map((t) => (
              <button key={t.seconds} data-testid={`trial-preset-${t.seconds}`} disabled={saving} onClick={() => saveSettings({ trial_seconds: t.seconds })}
                className={`rounded-lg border px-3 py-1.5 text-sm font-medium ${settings.trial_seconds === t.seconds ? "border-amber-500 bg-white" : "border-slate-200 bg-white hover:border-amber-300"}`}>{t.label}</button>
            ))}
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <span className="text-sm font-medium text-slate-700">Test mode (simulate charges — no real money):</span>
          <Button data-testid="test-mode-toggle" size="sm" variant={settings.test_mode ? "default" : "outline"} disabled={saving}
            onClick={() => saveSettings({ test_mode: !settings.test_mode })} className={settings.test_mode ? "bg-amber-600 hover:bg-amber-700" : ""}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (settings.test_mode ? "ON" : "OFF")}
          </Button>
        </div>
      </div>

      {/* Reports */}
      <ReportTable title="Trial users" testid="report-trialing" rows={report.trial_users} cols={["name", "email", "plan", "trial_ends_at"]} />
      <ReportTable title="Active subscriptions" testid="report-active" rows={report.active_subscriptions} cols={["name", "email", "plan", "next_billing_at"]} />
      <ReportTable title="Failed payments / suspended" testid="report-failed" rows={[...(report.past_due || []), ...(report.suspended || [])]} cols={["name", "email", "plan", "status", "charge_attempts"]} />
      <ReportTable title="Cancelled" testid="report-cancelled" rows={report.cancelled} cols={["name", "email", "plan"]} />
    </div>
  );
}

function ReportTable({ title, testid, rows, cols }) {
  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={testid}>
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <h2 className="font-heading font-bold tracking-tight">{title}</h2>
        <span className="text-sm text-slate-400">{rows?.length || 0}</span>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500"><tr>{cols.map((c) => <th key={c} className="text-left font-semibold px-4 py-3 capitalize">{c.replace(/_/g, " ")}</th>)}</tr></thead>
        <tbody className="divide-y divide-slate-100">
          {(!rows || rows.length === 0) && <tr><td colSpan={cols.length} className="py-8 text-center text-slate-400">None</td></tr>}
          {(rows || []).map((r, i) => (
            <tr key={i}>
              {cols.map((col) => (
                <td key={col} className="px-4 py-3 text-slate-700">
                  {col === "status" ? <StatusBadge status={r[col]} />
                    : col.endsWith("_at") ? (r[col] ? new Date(r[col]).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) : "—")
                    : (r[col] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
